import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { achievementsAPI, simhubAPI, resolveMediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Users, Star, Car, Gamepad2, Flag } from "lucide-react";
import OutlineTitle from "@/components/OutlineTitle";

const CATEGORY_LABEL = { gear: "Gear Brand", car: "Car Club", track: "Track", car_class: "Car Class", brand: "Brand", milestone: "Milestone" };

function clubKindIcon(club) {
  if (club.kind === "gear") return Gamepad2;
  if (club.kind === "track" || club.category === "track") return Flag;
  if (club.kind === "car" || club.category === "car_class") return Car;
  return Users;
}

function ClubCard({ club, joined, onJoin, onLeave, onOpen, disabled, canAdmin }) {
  const canOpen = club.__custom && (joined || canAdmin);
  const Icon = clubKindIcon(club);
  const img = resolveMediaUrl(club.cover_image || club.image || "");
  return (
    <Card className="border-border bg-card p-4" data-testid={`club-card-${club.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          {img ? (
            <img src={img} alt={club.name} className="h-11 w-11 flex-shrink-0 rounded-lg object-cover" />
          ) : (
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
              <Icon className="h-5 w-5" />
            </div>
          )}
          <div>
            <p className="font-medium">{club.name}{club.is_featured && <Star className="ml-1 inline h-3.5 w-3.5 text-accent" />}</p>
            <p className="text-xs text-muted-foreground">{club.member_count ?? club.members ?? 0} members</p>
          </div>
        </div>
        {(club.category || club.kind) && <Badge variant="secondary" className="text-xs capitalize">{CATEGORY_LABEL[club.kind] || CATEGORY_LABEL[club.category] || club.category}</Badge>}
      </div>
      {club.description && <p className="mt-2 text-sm text-muted-foreground">{club.description}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        {canOpen && (
          <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => onOpen(club)} data-testid={`club-open-${club.id}`}>
            Open club
          </Button>
        )}
        <Button
          size="sm"
          variant={joined ? "outline" : "default"}
          className={joined ? "" : (canOpen ? "" : "bg-primary text-primary-foreground hover:bg-primary/90")}
          onClick={() => (joined ? onLeave(club) : onJoin(club))}
          disabled={disabled}
          data-testid={`club-${joined ? "leave" : "join"}-${club.id}`}
        >
          {joined ? "Leave club" : disabled ? "Not eligible yet" : "Join club"}
        </Button>
      </div>
    </Card>
  );
}

export default function Clubs() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!(user?.is_admin || user?.is_super_admin);
  const [available, setAvailable] = useState([]);
  const [mine, setMine] = useState([]);
  const [custom, setCustom] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([
      achievementsAPI.getClubs().catch(() => ({ data: [] })),
      achievementsAPI.getMyClubs().catch(() => ({ data: [] })),
      simhubAPI.listCustomClubs().catch(() => ({ data: [] })),
    ]).then(([a, m, c]) => {
      setAvailable(Array.isArray(a.data) ? a.data : a.data?.clubs || []);
      setMine(Array.isArray(m.data) ? m.data : m.data?.clubs || []);
      setCustom(Array.isArray(c.data) ? c.data : []);
      setLoading(false);
    });
  };
  useEffect(() => load(), []);

  const myIds = new Set(mine.map((c) => c.id));

  const join = async (club) => {
    try {
      if (club.__custom) await simhubAPI.joinCustomClub(club.id);
      else await achievementsAPI.joinClub(club.id);
      toast.success("Joined club");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not join"); }
  };
  const leave = async (club) => {
    try {
      if (club.__custom) await simhubAPI.leaveCustomClub(club.id);
      else await achievementsAPI.leaveClub(club.id);
      toast.success("Left club");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not leave"); }
  };
  const openClub = (club) => navigate(`/clubs/${club.id}`);

  const customTagged = custom.map((c) => ({ ...c, __custom: true }));
  const allAvailable = [...available, ...customTagged];
  const myCustom = customTagged.filter((c) => c.is_member);
  const allMine = [...mine, ...myCustom];

  const isGear = (c) => c.kind === "gear" || c.category === "gear";
  const isCar = (c) => c.kind === "car" || c.category === "car_class";
  const carClubs = allAvailable.filter(isCar);
  const gearClubs = allAvailable.filter(isGear);

  const renderGrid = (list, emptyMsg, forceJoined = false) =>
    list.length ? (
      <div className="grid gap-3 sm:grid-cols-2">
        {list.map((c) => {
          const joined = forceJoined || (c.__custom ? !!c.is_member : myIds.has(c.id));
          const disabled = c.__custom && !c.is_member && !c.is_eligible;
          return <ClubCard key={`${c.__custom ? "cust-" : ""}${c.id}`} club={c} joined={joined} onJoin={join} onLeave={leave} onOpen={openClub} disabled={disabled} canAdmin={isAdmin} />;
        })}
      </div>
    ) : <p className="py-12 text-center text-sm text-muted-foreground">{emptyMsg}</p>;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <OutlineTitle>Clubs</OutlineTitle>

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-lg" />)}</div>
      ) : (
        <Tabs defaultValue="cars">
          <TabsList>
            <TabsTrigger value="cars" data-testid="clubs-tab-cars">Cars</TabsTrigger>
            <TabsTrigger value="gear" data-testid="clubs-tab-gear">Sim Gear</TabsTrigger>
            <TabsTrigger value="mine" data-testid="clubs-tab-mine">My Clubs ({allMine.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="cars" className="mt-4">
            {renderGrid(carClubs, "No car clubs available yet.")}
          </TabsContent>
          <TabsContent value="gear" className="mt-4">
            {renderGrid(gearClubs, "No sim gear clubs yet. Add your gear in your profile to unlock brand clubs.")}
          </TabsContent>
          <TabsContent value="mine" className="mt-4">
            {renderGrid(allMine, "You haven't joined any clubs yet.", true)}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
